import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllGame, getAllGames, getCashLeague } from "../../api/user";
import { SectionError } from "../../components/Skeleton/LoadError";
import { AF_EVENTS, logAppsFlyerEvent } from "../../utils/appsflyer";
import SectionHeader from "./landing/SectionHeader";
import FeaturedCashHeroCard from "./landing/FeaturedCashHeroCard";
import FeaturedCashCompactCard from "./landing/FeaturedCashCompactCard";
import PublicLeagueRow, { PANEL_BLEED } from "./landing/PublicLeagueRow";
import ClubChallengeCard from "./landing/ClubChallengeCard";
import MyLeaguesSection from "./landing/MyLeaguesSection";
import LeagueEmptyState, { EmptyStateArt } from "./landing/LeagueEmptyState";
import TileSkeleton, { FeaturedCashSkeleton } from "./landing/TileSkeleton";
import {
  SECTION_CAP,
  pickFeaturedCashGames,
  pickSectionLeagues,
} from "./landing/leaguePicker";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../components/Other/PullToRefreshIndicator";

// Both modals are behind a user action, so their code (and the wizards' entry
// points they link to) stays out of the landing bundle until it's needed.
const JoinByInviteCodeModal = lazy(() => import("./landing/JoinByInviteCodeModal"));
const CreateLeagueModal = lazy(() => import("./landing/CreateLeagueModal"));

const NOTIFY_KEY = "qushi_cash_game_notify";
const IDLE = { loading: true, error: false, data: [] };

/**
 * League Play home.
 *
 * Four sections, each built empty-state-first: Featured Cash Games, College
 * Club Challenges, Public Leagues, My Leagues. Mobile stacks them; desktop puts
 * the cash games and the club showdown full width, then splits Public Leagues
 * (wide) from My Leagues (narrow).
 *
 * Loading: the three requests are independent, so each section owns its own
 * loading/error state and paints the moment its own request lands. Nothing waits
 * on the slowest call, and a failed section shows a retry instead of pretending
 * to be empty.
 */
const LeaguePlayLanding = () => {
  const [cash, setCash] = useState(IDLE);
  const [publicLeagues, setPublicLeagues] = useState(IDLE);
  const [myLeagues, setMyLeagues] = useState(IDLE);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [notified, setNotified] = useState(
    () => localStorage.getItem(NOTIFY_KEY) === "1"
  );

  const loadCash = useCallback(() => {
    setCash((s) => ({ ...s, loading: true, error: false }));
    return getCashLeague()
      .then((res) =>
        setCash({ loading: false, error: false, data: res?.data?.data || [] })
      )
      .catch((e) => {
        console.error("Cash games load failed:", e);
        setCash({ loading: false, error: true, data: [] });
      });
  }, []);

  const loadPublic = useCallback(() => {
    setPublicLeagues((s) => ({ ...s, loading: true, error: false }));
    return getAllGame()
      .then((res) => {
        const all = res?.data?.data || [];
        const open = all.filter(
          (g) => g.game_type === "public" && !g.is_cash_prize_league
        );
        // Ordering/capping is leaguePicker's job — keep the raw list here so
        // "View all" and this section can't disagree about what exists.
        setPublicLeagues({ loading: false, error: false, data: open });
      })
      .catch((e) => {
        console.error("Public leagues load failed:", e);
        setPublicLeagues({ loading: false, error: true, data: [] });
      });
  }, []);

  const loadMine = useCallback(() => {
    setMyLeagues((s) => ({ ...s, loading: true, error: false }));
    return getAllGames()
      .then((res) =>
        setMyLeagues({ loading: false, error: false, data: res?.data?.data || [] })
      )
      .catch((e) => {
        console.error("My leagues load failed:", e);
        setMyLeagues({ loading: false, error: true, data: [] });
      });
  }, []);

  useEffect(() => {
    loadCash();
    loadPublic();
    loadMine();
  }, [loadCash, loadPublic, loadMine]);

  // All three sections load independently (see the note above the component),
  // so refresh re-fires all three rather than picking one.
  const refreshAll = useCallback(
    () => Promise.all([loadCash(), loadPublic(), loadMine()]),
    [loadCash, loadPublic, loadMine]
  );

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshAll,
    disabled: false,
  });

  // Arriving at /contest#my-leagues (from the created-league payoff) should land
  // ON that section. React Router doesn't scroll to a hash by itself, and the
  // sections above this one change height as their own requests land — so wait
  // until My Leagues has its data before measuring, or the scroll aims at a
  // position the page has already moved past.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#my-leagues" || myLeagues.loading) return undefined;
    const id = window.requestAnimationFrame(() => {
      document
        .getElementById("my-leagues")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [hash, myLeagues.loading]);

  const handleNotifyMe = () => {
    // Fired on the tap itself, before anything else — the client measures the
    // intent, so it must not depend on the local state write succeeding.
    logAppsFlyerEvent(AF_EVENTS.CASH_GAME_NOTIFY_TAP);
    localStorage.setItem(NOTIFY_KEY, "1");
    setNotified(true);
    toast.success("We'll let you know as soon as the next cash game opens.");
  };

  // Section caps + which leagues fill them live in leaguePicker.js:
  //   Cash Games — the two most recently PUBLISHED, newest in the hero. A third
  //     game is not dropped: it is what "View all" lists.
  //   Public / My Leagues — active first, then upcoming, completed fills (cap 2)
  const { hero, secondary } = pickFeaturedCashGames(cash.data);
  // Completed leagues are My Leagues' business only — see pickSectionLeagues.
  const publicPicks = pickSectionLeagues(publicLeagues.data, SECTION_CAP, false);
  const myPicks = pickSectionLeagues(myLeagues.data, SECTION_CAP);

  return (
    // The mobile bottom nav is fixed and 64px tall (BottomNavbar is `h-16`,
    // `md:hidden`), so 48px of padding left the last card clipped under it with
    // nowhere left to scroll. pb-24 clears the nav with a gap below the card —
    // the same value the Leaderboard uses for the same reason. Desktop has no
    // bottom nav, so its padding is unchanged.
    <div className="pb-24 md:pb-16" {...containerProps}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        {/* House rule: flagship page title — title-page token at every breakpoint,
            same as Leaderboard. The smaller title-subpage token is for sub-pages. */}
        <h1 className="font-montserrat font-bold text-title-page text-pageTitle">
          League Play
        </h1>
        {/* Client Rev 4 (desktop polish): title -> this line about 10px; the two
            supporting lines 2-4px apart. Mobile spacing is untouched. */}
        <p className="mt-1.5 md:mt-2.5 font-semibold text-sm md:text-base text-[#0F4146]">
          Skill-based competitions.
        </p>
        {/* "We changed this line to a shorter line on mobile. The line above
            should not have changed for the desktop version. Please keep the
            mobile as is and change only the desktop back." */}
        <p className="md:mt-0.5 font-semibold text-sm md:text-base text-[#22A36B]">
          <span className="md:hidden">
            League results don't affect your main record.
          </span>
          <span className="hidden md:inline">
            League results stay separate from your main record.
          </span>
        </p>

        {/* Full-width CTA at every breakpoint — it spans the content column on
            desktop too, per the design. */}
        {/* Desktop polish: ~24px from the green line to the button, a 56px button,
            and 12-14px from it down to the invite line. */}
        <div className="mt-4 md:mt-6">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="w-full rounded-xl bg-[#0F4146] hover:bg-[#0B3A3F] text-white py-3.5 md:py-0 md:h-14 font-bold text-base md:text-lg flex items-center justify-center transition-colors"
          >
            Create a League
          </button>

          <p className="mt-2.5 md:mt-3 text-center text-sm text-secondary">
            Got an invite code?{" "}
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="font-semibold text-[#0F4146] hover:underline"
            >
              Get in.
            </button>
          </p>
        </div>

        {/* ---------------------------------------------------- Featured cash */}
        {/* Desktop polish: 42-48px from the invite line to this heading. */}
        <section className="mt-7 md:mt-11">
          <SectionHeader
            title="Featured Cash Games"
            viewAllTo={hero ? "/contest/cash-leagues" : undefined}
          />
          {cash.loading ? (
            <FeaturedCashSkeleton />
          ) : cash.error ? (
            <div className="rounded-2xl bg-white border border-gray-200 px-5">
              <SectionError
                message="Cash games could not be loaded."
                onRetry={loadCash}
              />
            </div>
          ) : hero ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 items-stretch">
              {/* One game on its own takes the full row rather than leaving a
                  third of the desktop grid empty. */}
              <div className={secondary ? "lg:col-span-2" : "lg:col-span-3"}>
                <FeaturedCashHeroCard game={hero} />
              </div>
              {secondary && (
                <div className="lg:col-span-1">
                  <FeaturedCashCompactCard game={secondary} />
                </div>
              )}
            </div>
          ) : (
            <LeagueEmptyState
              art={<EmptyStateArt variant="leagues" />}
              title="No cash games open"
              body={["New games drop regularly.", "Check back soon."]}
              actionLabel={notified ? "You're on the list" : "Notify Me"}
              onAction={handleNotifyMe}
              done={notified}
            />
          )}
        </section>

        {/* ------------------------------------------------- Club challenges */}
        <section className="mt-7 md:mt-9">
          <SectionHeader title="College Club Challenges" />
          <ClubChallengeCard />
        </section>

        {/* --------------------------------- Public leagues + my leagues */}
        {/* Desktop polish: 40-48px down from the College Club Challenges card, and
            `items-start` so Public Leagues and My Leagues begin at the exact same
            vertical position however tall either column grows. */}
        <div className="mt-7 md:mt-11 grid grid-cols-1 lg:grid-cols-3 items-start gap-7 lg:gap-6">
          <section className="lg:col-span-2">
            <SectionHeader
              title="Public Leagues"
              viewAllTo={publicPicks.length > 0 ? "/contest/public-leagues" : undefined}
            />
            {publicLeagues.loading ? (
              <TileSkeleton count={2} cols={2} />
            ) : publicLeagues.error ? (
              <div className="rounded-2xl bg-white border border-gray-200 px-5">
                <SectionError
                  message="Public leagues could not be loaded."
                  onRetry={loadPublic}
                />
              </div>
            ) : publicPicks.length > 0 ? (
              // Rows in a panel, not a grid of cards — the league preview is
              // one design sitewide and this is it. Hairlines between rows, the
              // same as the browse lists.
              <div className="rounded-2xl border border-gray-200 bg-white px-4 md:px-5">
                <div className="divide-y divide-divider">
                  {publicPicks.map((g) => (
                    <PublicLeagueRow key={g.id} game={g} bleed={PANEL_BLEED.inset} />
                  ))}
                </div>
              </div>
            ) : (
              <LeagueEmptyState
                art={<EmptyStateArt variant="leagues" />}
                title="No public leagues open"
                body="No leagues running. You could fix that."
                actionLabel="Create a Public League"
                to="/contest/create-public-league"
              />
            )}
          </section>

          <div className="lg:col-span-1">
            {myLeagues.error ? (
              <section>
                <SectionHeader title="My Leagues" />
                <div className="rounded-2xl bg-white border border-gray-200 px-5">
                  <SectionError
                    message="Your leagues could not be loaded."
                    onRetry={loadMine}
                  />
                </div>
              </section>
            ) : (
              <MyLeaguesSection leagues={myPicks} loading={myLeagues.loading} />
            )}
          </div>
        </div>
      </div>

      {/* Mounted only once opened, so neither modal costs anything on first
          paint. `null` fallback: the chunk lands in a frame or two and a
          skeleton flashing behind a modal reads as a glitch. */}
      {inviteOpen && (
        <Suspense fallback={null}>
          <JoinByInviteCodeModal open onClose={() => setInviteOpen(false)} />
        </Suspense>
      )}
      {createOpen && (
        <Suspense fallback={null}>
          <CreateLeagueModal open onClose={() => setCreateOpen(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default LeaguePlayLanding;
